import type { QueryClient } from '@tanstack/react-query';

const SYNC_QUEUE_DB_NAME = 'runtime-shell-sync-queue';
const SYNC_QUEUE_STORE = 'mutations';
const SYNC_QUEUE_VERSION = 1;
export const SYNC_QUEUE_EVENT = 'runtime-shell:sync-queue-changed';

export type SyncMutationType = 'create' | 'update' | 'delete';

interface SyncQueueBase {
  entityPath: string;
  endpoint: string;
  authToken?: string;
}

export interface SyncQueueCreateItem extends SyncQueueBase {
  mutationType: 'create';
  payload: Record<string, unknown>;
  optimisticId: string;
}

export interface SyncQueueUpdateItem extends SyncQueueBase {
  mutationType: 'update';
  recordId: string;
  payload: Record<string, unknown>;
}

export interface SyncQueueDeleteItem extends SyncQueueBase {
  mutationType: 'delete';
  recordId: string;
}

export type SyncQueueMutation = SyncQueueCreateItem | SyncQueueUpdateItem | SyncQueueDeleteItem;

export type SyncQueueItem = SyncQueueMutation & {
  id: number;
  createdAt: number;
};

interface DrainResult {
  succeeded: number;
  failed: number;
}

interface DrainOptions {
  queryClient: QueryClient;
  onResolved?: (item: SyncQueueItem) => void;
}

function withAuthHeaders(token?: string): HeadersInit {
  if (!token) {
    return { 'Content-Type': 'application/json' };
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function emitQueueChanged() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(SYNC_QUEUE_EVENT));
}

function openSyncQueueDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available in this environment.'));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SYNC_QUEUE_DB_NAME, SYNC_QUEUE_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
        db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open sync queue database.'));
  });
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
  });
}

function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  return error.name === 'TypeError' || /network|fetch|offline/i.test(error.message);
}

export class SyncQueue {
  async enqueue(item: SyncQueueMutation): Promise<number> {
    const db = await openSyncQueueDb();

    try {
      const tx = db.transaction(SYNC_QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(SYNC_QUEUE_STORE);
      const id = await promisifyRequest(store.add({ ...item, createdAt: Date.now() }));
      emitQueueChanged();
      return Number(id);
    } finally {
      db.close();
    }
  }

  async list(): Promise<SyncQueueItem[]> {
    const db = await openSyncQueueDb();

    try {
      const tx = db.transaction(SYNC_QUEUE_STORE, 'readonly');
      const store = tx.objectStore(SYNC_QUEUE_STORE);
      const items = await promisifyRequest(store.getAll()) as SyncQueueItem[];
      return items.sort((left, right) => left.id - right.id);
    } finally {
      db.close();
    }
  }

  async count(): Promise<number> {
    const db = await openSyncQueueDb();

    try {
      const tx = db.transaction(SYNC_QUEUE_STORE, 'readonly');
      const store = tx.objectStore(SYNC_QUEUE_STORE);
      return promisifyRequest(store.count());
    } finally {
      db.close();
    }
  }

  async remove(id: number): Promise<void> {
    const db = await openSyncQueueDb();

    try {
      const tx = db.transaction(SYNC_QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(SYNC_QUEUE_STORE);
      await promisifyRequest(store.delete(id));
      emitQueueChanged();
    } finally {
      db.close();
    }
  }

  async drain({ queryClient, onResolved }: DrainOptions): Promise<DrainResult> {
    const items = await this.list();
    let succeeded = 0;
    let failed = 0;

    for (const item of items) {
      try {
        if (item.mutationType === 'create') {
          const response = await fetch(item.endpoint, {
            method: 'POST',
            headers: withAuthHeaders(item.authToken),
            body: JSON.stringify(item.payload),
          });

          if (!response.ok) {
            throw new Error(`Create failed with status ${response.status}`);
          }
        }

        if (item.mutationType === 'update') {
          const response = await fetch(`${item.endpoint}/${item.recordId}`, {
            method: 'PUT',
            headers: withAuthHeaders(item.authToken),
            body: JSON.stringify(item.payload),
          });

          if (!response.ok) {
            throw new Error(`Update failed with status ${response.status}`);
          }
        }

        if (item.mutationType === 'delete') {
          const response = await fetch(`${item.endpoint}/${item.recordId}`, {
            method: 'DELETE',
            headers: withAuthHeaders(item.authToken),
          });

          if (!response.ok) {
            throw new Error(`Delete failed with status ${response.status}`);
          }
        }

        await this.remove(item.id);
        await queryClient.invalidateQueries({ queryKey: ['entity-data', item.entityPath] });
        onResolved?.(item);
        succeeded += 1;
      } catch (error) {
        failed += 1;

        if (isNetworkFailure(error)) {
          break;
        }
      }
    }

    return { succeeded, failed };
  }
}

export const syncQueue = new SyncQueue();

export function isOfflineMutationError(error: unknown): boolean {
  return isNetworkFailure(error);
}
